import { FC } from 'react'
import { DateTime } from 'luxon'
import { secondsToHHMM } from '@utils/time'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/pro-regular-svg-icons'
import { useDateTimeToLocaleString } from '@hooks/useDateTimeWithLocale'
import { Status } from 'types'
import { StatusBadge } from 'components/indicators/status-badge'

interface Props {
  displayPath: string[]
  endTime: DateTime
  projectIntegrationCode?: string
  projectName: string
  startTime: DateTime
  status?: Status
  taskName: string
  timeZone?: string
  workdayName?: string
}

export const TimeBlock: FC<Props> = ({
  displayPath,
  endTime,
  projectIntegrationCode,
  projectName,
  startTime,
  status,
  taskName,
  timeZone,
  workdayName,
}) => {
  const toLocaleString = useDateTimeToLocaleString()(DateTime.TIME_SIMPLE)
  // Render in the Time Card's time zone, not the signed in User's
  const formatDateTime = (dateTime: DateTime) =>
    toLocaleString(timeZone ? dateTime.setZone(timeZone) : dateTime)
  const durationString = secondsToHHMM(endTime.diff(startTime).as('seconds'))

  return (
    <div
      className="flex flex-col gap-2 text-neutral-900 overflow-hidden"
      data-testid="time-block"
    >
      <span className="text-[13px] font-medium">
        {formatDateTime(startTime)} - {formatDateTime(endTime)}
      </span>
      <div className="flex flex-col bg-neutral-50 border border-neutral-200 rounded-md p-2">
        <div className="flex flex-row flex-nowrap items-center max-w-full overflow-hidden gap-2 text-sm px-3 py-1 border-b border-neutral-200">
          <span className="uppercase font-semibold truncate">
            {projectName}
          </span>
          {projectIntegrationCode && (
            <span className="uppercase truncate">
              ({projectIntegrationCode})
            </span>
          )}
          {workdayName && (
            <span className="ml-auto text-neutral-600">{workdayName}</span>
          )}
          {status && <StatusBadge status={status} />}
          <div className="ml-auto text-neutral-600 flex text-xs flex-row items-center">
            <FontAwesomeIcon className="mr-1" icon={faClock} />
            {durationString}
          </div>
        </div>
        <div className="flex flex-col px-3 pt-1 gap-1">
          <span className="text-neutral-600 font-medium text-xs">
            {displayPath.join(' • ')}
          </span>
          <span className="font-semibold text-sm">{taskName}</span>
        </div>
      </div>
    </div>
  )
}
